import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import type {
    ImageChooserBlock,
    SharedCompetitionCompareTableBlock as Value,
} from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import {
    getImageSrc,
    hasRichTextContent,
} from "../../utils/streamfieldContent";

type IProps = Immutable<{
    value: Value;
}>;

type Feature = NonNullable<Value["features"]>[number];
type FeatureStatus = Feature["our_feature_status"];

/**
 * Faithful port of the legacy server-rendered
 * ``tsicommon_cms/blocks/competition-compare-table-block.html``.
 *
 * This is the SHARED ("base") variant -- the one Cocoon's toolbox uses. The
 * legacy template branched on two ``get_context`` flags that are NOT set for
 * this base block:
 *
 *   - ``logo_is_richtext``  -- false here, so the logo is rendered as an
 *     ``<img>`` from the ``logo`` ImageChooserBlock (the richtext-logo branch
 *     belongs to a site-specific subclass).
 *   - ``use_separate_cta_row`` -- false here, so there is NO standalone CTA row;
 *     the CTA renders INLINE inside the last feature row's "our" cell. The
 *     separate-CTA-row + competitor-CTA path is Stearns-only and is not part of
 *     this type.
 *
 * Likewise the ``highlight--*`` / ``highlight__competitor-column--*`` classes on
 * ``<table>`` come from ``our_column_background_color`` /
 * ``competitor_column_background_color`` fields that exist only on the
 * Sealy/Stearns subclasses -- absent here, so the table carries an empty class
 * (matching the legacy ``<table class="">`` output).
 *
 * Richtext fields (``competitor_name``, ``second_competitor_name``,
 * ``fineprint``) serialize to an already-``.rich-text``-wrapped HTML string (the
 * legacy used ``{% include_block %}``). We therefore raw-inject them via
 * ``dangerouslySetInnerHTML`` directly into their container, matching the
 * convention established by ``ProductComparePrintOnlyBlock`` (wrapping them in
 * ``<RichTextBlock>`` would add an extra wrapper div not present in the
 * baseline). ``feature_name`` / ``cta_text`` are plain CharBlocks (text nodes).
 *
 * Image ``src`` resolution lives in the shared ``getImageSrc`` helper
 * (``../utils``): prefer the inlined ``imageData`` data-URI, else the widest
 * rendition, else the raw ``url``.
 */

const IconImage = (
    props: Immutable<{
        image: ImageChooserBlock | null;
        alt: string;
    }>,
): React.JSX.Element | null => {
    if (!props.image) {
        return null;
    }
    return (
        <img
            src={getImageSrc(props.image)}
            alt={props.alt}
            loading="lazy"
            decoding="async"
        />
    );
};

const getLegacyRichTextHtml = (html: string | null | undefined): string =>
    (html ?? "").replace(/<\/br\s*>/gi, "<br />");

/**
 * Render one status cell for a column. ``isOurColumn`` selects the affirmative
 * icon (``our_affirmative_icon`` vs ``affirmative_icon``) and, for the last row,
 * gates the inline CTA -- both mirror the legacy template exactly.
 */
const StatusCell = (
    props: Immutable<{
        value: Value;
        status: FeatureStatus;
        text: string | null | undefined;
        isOurColumn: boolean;
        isLast: boolean;
    }>,
): React.JSX.Element | null => {
    const v = props.value;
    const affirmativeIcon = props.isOurColumn
        ? v.our_affirmative_icon
        : v.affirmative_icon;
    // Inline CTA: only on the last row, only in the "our" column, only for the
    // 'yes' and 'text' branches (legacy never emitted it in the 'no' branch).
    const cta =
        props.isOurColumn && props.isLast && v.cta_destination ? (
            <a className="button" href={v.cta_destination.url}>
                {v.cta_text}
            </a>
        ) : null;

    if (props.status === "yes") {
        return (
            <td>
                {props.text ? (
                    <div>
                        <IconImage
                            image={affirmativeIcon}
                            alt={t`We have it`}
                        />
                        {props.text}
                    </div>
                ) : (
                    <IconImage image={affirmativeIcon} alt={t`We have it`} />
                )}
                {cta}
            </td>
        );
    }
    if (props.status === "no") {
        return (
            <td>
                {props.text ? (
                    <div>
                        <IconImage
                            image={v.negative_icon}
                            alt={t`We don't have it`}
                        />
                        {props.text}
                    </div>
                ) : (
                    <IconImage
                        image={v.negative_icon}
                        alt={t`We don't have it`}
                    />
                )}
            </td>
        );
    }
    if (props.status === "text") {
        return (
            <td>
                {props.text}
                {cta}
            </td>
        );
    }
    // status is null / "" -> no cell rendered (legacy emitted nothing).
    return null;
};

/**
 * The second-competitor column never carries feature text or a CTA in the
 * legacy markup -- only a bare yes/no icon or plain text.
 */
const SecondCompetitorCell = (
    props: Immutable<{
        value: Value;
        status: NonNullable<Feature["second_competition_feature_status"]>;
        text: string | null | undefined;
    }>,
): React.JSX.Element | null => {
    const v = props.value;
    if (props.status === "yes") {
        return (
            <td>
                <IconImage image={v.affirmative_icon} alt={t`We have it`} />
            </td>
        );
    }
    if (props.status === "no") {
        return (
            <td>
                <IconImage image={v.negative_icon} alt={t`We don't have it`} />
            </td>
        );
    }
    if (props.status === "text") {
        return <td>{props.text}</td>;
    }
    return null;
};

export const CompetitionCompareTableBlock = (
    props: IProps,
): React.JSX.Element => {
    const v = props.value;
    const features = v.features ?? [];
    const hasSecondCompetitor = hasRichTextContent(v.second_competitor_name);
    return (
        <section
            className={concatClassNames([
                "competition-compare-table-block",
                v.background_style?.style,
                v.responsive_visibility?.visibility,
                v.theme,
                hasSecondCompetitor
                    ? "competition-compare-table-block--four-columns"
                    : "",
                v.cta_destination
                    ? "competition-compare-table-block--with-cta"
                    : "",
            ])}
        >
            <table className="">
                <thead>
                    <tr>
                        <th scope="col">
                            <p className="ada-screenreader-only">{t`Features`}</p>
                        </th>
                        <th scope="col">
                            {v.logo && (
                                <img
                                    src={getImageSrc(v.logo)}
                                    alt={v.logo.default_alt_text}
                                    loading="lazy"
                                    decoding="async"
                                />
                            )}
                        </th>
                        <th
                            scope="col"
                            dangerouslySetInnerHTML={{
                                __html: getLegacyRichTextHtml(
                                    v.competitor_name,
                                ),
                            }}
                        />
                        {hasSecondCompetitor && (
                            <th
                                scope="col"
                                dangerouslySetInnerHTML={{
                                    __html: getLegacyRichTextHtml(
                                        v.second_competitor_name,
                                    ),
                                }}
                            />
                        )}
                    </tr>
                </thead>
                <tbody>
                    {features.map((feature, i) => {
                        const isLast = i === features.length - 1;
                        return (
                            // ListBlock items expose no stable id; the legacy
                            // render was positional (forloop), so an index key
                            // matches that ordering.
                            <tr key={i} className={isLast ? "last no-cta" : ""}>
                                <th scope="row">{feature.feature_name}</th>
                                <StatusCell
                                    value={v}
                                    status={feature.our_feature_status}
                                    text={feature.our_feature_text}
                                    isOurColumn={true}
                                    isLast={isLast}
                                />
                                <StatusCell
                                    value={v}
                                    status={feature.competition_feature_status}
                                    text={feature.competition_feature_text}
                                    isOurColumn={false}
                                    isLast={isLast}
                                />
                                {hasSecondCompetitor && (
                                    <SecondCompetitorCell
                                        value={v}
                                        status={
                                            feature.second_competition_feature_status ??
                                            ""
                                        }
                                        text={
                                            feature.second_competition_feature_text
                                        }
                                    />
                                )}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {v.fineprint && (
                <div
                    className="fineprint"
                    dangerouslySetInnerHTML={{
                        __html: getLegacyRichTextHtml(v.fineprint),
                    }}
                />
            )}
        </section>
    );
};
